import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import cx from 'classnames';
import {
  BoldTool,
  ItalicTool,
  UnderlineTool,
  LeftAlignTool,
  CenterAlignTool,
  RightAlignTool,
  UnorderedListTool,
  OrderedListTool,
  IncreaseIndentTool,
  DecreaseIndentTool,
  CopyInlineStyleTool,
  ClearFormattingTool,
  UndoTool,
  RedoTool,
  EmojiTool,
  ImageTool,
  VideoTool,
  LinkTool,
  withToolbarOnChangeEnhancers,
  imageRichUtils,
  showRichTextSelectionHighlightAction,
} from '@nextiva/rich-text-editor';

import FontTextEditorTool from '../FontTextEditorTool';
import LineHeightTextEditorTool from '../LineHeightTextEditorTool';
import FontSizeTextEditorTool from '../FontSizeTextEditorTool';
import ColorTextEditorTool from '../ColorTextEditorTool';

import {
  openCustomLineHeightModal,
  openInsertImageIntoEditorModal,
  openInsertImageLinkIntoEditorModal,
  openInsertLinkIntoEditorModal,
  openInsertVideoIntoEditorModal,
} from '../../../../../actions/richTextEditorModalActions';
import { getMaxWidth } from '../../../config/editorWidthUtils';
import presetNameList from '../../../config/presetNameList';
import configPreset from '../../../config/configPreset';

import styles from './RichTextEditorToolbar.scss';

const toolStyles = {
  className: styles.tool,
  activeClassName: styles.activeTool,
  disabledClassName: styles.disabled,
};

class RichTextEditorToolbar extends Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    editorState: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    onInsertLink: PropTypes.func.isRequired,
    onInsertImageLink: PropTypes.func.isRequired,
    onInsertImage: PropTypes.func.isRequired,
    onInsertVideo: PropTypes.func.isRequired,
    openCustomLineHeightModal: PropTypes.func.isRequired,
    highlightSelection: PropTypes.func.isRequired,
    defaultFontSize: PropTypes.number,
    defaultFont: PropTypes.string,
    defaultFontColor: PropTypes.string,
    preset: PropTypes.string,
    className: PropTypes.string,
    editorLayout: PropTypes.string,
    style: PropTypes.object,
  };

  static defaultProps = {
    defaultFontSize: null,
    defaultFont: null,
    defaultFontColor: null,
    preset: presetNameList.full,
  };

  maxWidth = getMaxWidth(this.props.editorLayout);

  handleInsertLinkClick = selectionLink => {
    return imageRichUtils.isImageBlockSelected(this.props.editorState)
      ? this.handleInsertImageLink()
      : this.handleInsertLink(selectionLink);
  };

  handleInsertLink = ({ url, label }) => {
    const { id, onInsertLink } = this.props;

    onInsertLink(id, { url, label });
  };

  handleInsertImageLink() {
    const { id, editorState, onInsertImageLink } = this.props;
    const link = imageRichUtils.getSelectedImageLinkUrl(editorState);

    onInsertImageLink(id, link);
  }

  insertImage = () => this.props.onInsertImage(this.props.id);

  insertVideo = () => this.props.onInsertVideo(this.props.id);

  handleCustomLineHeightClick = ({
    currentLineHeight,
    currentBlockSpacing,
  }) => {
    const { highlightSelection, openCustomLineHeightModal } = this.props;

    highlightSelection(this.props.id);
    openCustomLineHeightModal(
      this.props.id,
      currentLineHeight,
      currentBlockSpacing,
    );
  };

  render = () => {
    const {
      id,
      editorState,
      onChange,
      defaultFontSize,
      defaultFont,
      defaultFontColor,
      preset,
      className,
      style,
    } = this.props;

    if (!editorState) {
      return null;
    }

    const { tools } = configPreset[preset];

    return (
      <div style={style} className={cx(styles.toolbar, className)}>
        <div className={styles.controlGroup}>
          <FontTextEditorTool
            e2e="tool-font-family"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            customFontSupported
            defaultFont={defaultFont || configPreset[preset].defaultFont}
            disabledClassName={styles.disabled}
            {...tools['font']}
          />
          <FontSizeTextEditorTool
            e2e="tool-font-size"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            defaultFontSize={
              defaultFontSize || configPreset[preset].defaultFontSize
            }
            disabledClassName={styles.disabled}
            {...tools['fontSize']}
          />
          <ColorTextEditorTool
            e2e="tool-text-color"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            defaultFontColor={
              defaultFontColor || configPreset[preset].defaultFontColor
            }
            {...toolStyles}
            {...tools['fontColor']}
          />
          <BoldTool
            e2e="tool-bold"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            {...toolStyles}
            {...tools['bold']}
          />
          <ItalicTool
            e2e="tool-italic"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            {...toolStyles}
            {...tools['italic']}
          />
          <UnderlineTool
            e2e="tool-underline"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            {...tools['underline']}
            {...toolStyles}
          />
          <LineHeightTextEditorTool
            e2e="tool-line-height"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            onCustomOptionClick={this.handleCustomLineHeightClick}
            disabledClassName={styles.disabled}
            {...tools['lineHeight']}
          />
        </div>
        <div className={styles.controlGroup}>
          <LeftAlignTool
            e2e="tool-align-left"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            {...toolStyles}
            {...tools['leftAlign']}
          />
          <CenterAlignTool
            e2e="tool-align-center"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            {...toolStyles}
            {...tools['centerAlign']}
          />
          <RightAlignTool
            e2e="tool-align-right"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            {...toolStyles}
            {...tools['rightAlign']}
          />
        </div>
        <div className={styles.controlGroup}>
          <UnorderedListTool
            e2e="tool-list"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            {...toolStyles}
            {...tools['unorderedList']}
          />
          <OrderedListTool
            e2e="tool-list-ordered"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            {...toolStyles}
            {...tools['orderedList']}
          />
          <IncreaseIndentTool
            e2e="tool-ident-increase"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            maxWidth={this.maxWidth}
            {...toolStyles}
            {...tools['increaseIndent']}
          />
          <DecreaseIndentTool
            e2e="tool-ident-decrease"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            {...toolStyles}
            {...tools['decreaseIndent']}
          />
        </div>
        <div className={styles.controlGroup}>
          <LinkTool
            e2e="tool-link"
            editorState={editorState}
            onClick={this.handleInsertLinkClick}
            {...toolStyles}
            {...tools['link']}
          />
          <ImageTool
            e2e="tool-image"
            onClick={this.insertImage}
            {...toolStyles}
            {...tools['image']}
          />
          <VideoTool
            e2e="tool-video"
            onClick={this.insertVideo}
            {...toolStyles}
            {...tools['video']}
          />
          <CopyInlineStyleTool
            e2e="tool-copy-style"
            editorId={id}
            editorState={editorState}
            {...toolStyles}
            {...tools['copyInlineStyle']}
          />
        </div>
        <div className={styles.controlGroup}>
          <ClearFormattingTool
            e2e="tool-clear-formatting"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            {...toolStyles}
            {...tools['clearInlineStyle']}
          />
          <UndoTool
            e2e="tool-undo"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            {...toolStyles}
            {...tools['undo']}
          />
          <RedoTool
            e2e="tool-redo"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            {...toolStyles}
            {...tools['redo']}
          />
          <EmojiTool
            e2e="tool-emoji"
            editorId={id}
            editorState={editorState}
            onChange={onChange}
            {...toolStyles}
            {...tools['emoji']}
          />
        </div>
      </div>
    );
  };
}

const mapDispatchToProps = {
  onInsertImage: openInsertImageIntoEditorModal,
  onInsertVideo: openInsertVideoIntoEditorModal,
  onInsertLink: openInsertLinkIntoEditorModal,
  onInsertImageLink: openInsertImageLinkIntoEditorModal,
  openCustomLineHeightModal,
  highlightSelection: showRichTextSelectionHighlightAction,
};

export default compose(
  connect(
    null,
    mapDispatchToProps,
  ),
  withToolbarOnChangeEnhancers,
)(RichTextEditorToolbar);
